import { addErrorsAction } from "../../../actions/errors";
import * as actionTypes from "../../../constants";
import { DomainType } from "../../../models";
import { encounterAPI } from "../../../services/encounter";
import { parseServiceErrors } from "../../../services/encounter/serviceErrors";

import type { SortDescriptor } from "@progress/kendo-data-query";
import type { AnyAction } from "redux";
import type { ThunkAction } from "redux-thunk";
import type { DomainTypeEnum, UpdateRecoveredCountPayload } from "../../../models";
import type { State } from "../../../reducers";

export const mapDomainToRecoveryState = {
  [DomainType.Facility]: "totalFacility",
  [DomainType.Practice]: "totalPractice",
};

const mapSortList = {
  accountNumber: ["AccountNumber"],
  mrn: ["MedicalRecordNumber"],
  fullName: ["FirstName", "MiddleInitial", "LastName"],
  dateOfBirthAge: ["DateOfBirth", "PatientAge"],
  lastUpdated: ["LastUpdated"],
  lastDateToRecover: ["LastUpdated"],
};

export const prepareSortToRequest = (sort: SortDescriptor[]) =>
  sort?.reduce<Record<string, number>>((acc, item, i) => {
    const direction = item.dir === "desc" ? "SortDesc" : "SortAsc";
    const fields = mapSortList[item.field] ?? mapSortList.lastUpdated;
    const sortObj = {};
    fields.forEach((field, idx) => {
      sortObj[`${direction}.${field}`] = i + idx;
    });

    return {
      ...acc,
      ...sortObj,
    };
  }, {});

export const updateRecoveredEncountersTotal = (info: UpdateRecoveredCountPayload) => ({
  payload: info,
  type: actionTypes.UPDATE_RECOVERED_ENCOUNTERS_TOTAL,
});

export const updateRecoveredDomainTotalAction =
  (domain: DomainTypeEnum, count: number): ThunkAction<void, State, unknown, AnyAction> =>
  (dispatch) =>
    dispatch(
      updateRecoveredEncountersTotal({
        [mapDomainToRecoveryState[domain]]: count,
      }),
    );

export const getRecoveredEncountersCountAction =
  (): ThunkAction<void, State, unknown, AnyAction> => async (dispatch, getState) => {
    const features = getState().userFeatures.packages;
    const hasFACFeature = features?.FAC === true;
    const hasPSAFeature = features?.PSA === true;

    try {
      const getFacilitiesCount =
        hasFACFeature ? encounterAPI.getRecoveredEncountersCount(DomainType.Facility) : null;
      const getPracticesCount =
        hasPSAFeature ? encounterAPI.getRecoveredEncountersCount(DomainType.Practice) : null;

      const [facResult, psaResult] = await Promise.all([getFacilitiesCount, getPracticesCount]);

      dispatch(
        updateRecoveredEncountersTotal({
          totalFacility: facResult?.TotalCount ?? 0,
          totalPractice: psaResult?.TotalCount ?? 0,
        }),
      );
    } catch (reason) {
      const errors = parseServiceErrors(
        reason as Error,
        "Cannot Retrieve Recovered Encounters Count",
      );
      dispatch(addErrorsAction(errors));
    }
  };

export const checkEncounterIsRecoveredAction =
  (encounterId: string): ThunkAction<void, State, unknown, AnyAction> =>
  (dispatch) =>
    encounterAPI
      .checkEncounterIsRecovered(encounterId)
      .then((recovered) => recovered)
      .catch((reason) => {
        const errors = parseServiceErrors(reason, "Cannot Check Encounter Status");
        dispatch(addErrorsAction(errors));
        return undefined;
      });
